import shopifyAdminFetch from "./shopifyAdminFetch";

// Limits imposed by the Shopify Admin GraphQL API (products.first / variants.first = 250 max)
const SHOPIFY_PAGE_SIZE = 250;

// Backoff settings for THROTTLED responses from Shopify Admin GraphQL
const MAX_THROTTLE_RETRIES = 5;
const INITIAL_BACKOFF_MS = 1000;
const MAX_BACKOFF_MS = 16000;

// Safety cap on variants per product (matches backend schema `maxItems: 2048`)
export const MAX_VARIANTS_PER_PRODUCT = 2048;

const isThrottled = (response) =>
  Array.isArray(response?.errors) && response.errors.some((e) => e?.extensions?.code === "THROTTLED");

const isNetworkError = (error) => {
  const msg = String(error?.message || error || "");
  return /throttl|rate limit|429/i.test(msg);
};

// Wrap a Shopify Admin GraphQL call with exponential backoff on throttling.
// Also applies a small pre-emptive sleep when the bucket is running low, to
// reduce the chance of a hard throttle mid-pagination.
export async function withShopifyBackoff(fn) {
  for (let attempt = 0; ; attempt++) {
    try {
      const response = await fn();

      if (isThrottled(response)) {
        if (attempt >= MAX_THROTTLE_RETRIES) {
          throw new Error("Shopify Admin API throttled (max retries exceeded)");
        }
      } else {
        const available = response?.extensions?.cost?.throttleStatus?.currentlyAvailable;
        if (typeof available === "number" && available < 200) {
          await sleep(1500);
        }
        return response;
      }
    } catch (error) {
      if (!isNetworkError(error) || attempt >= MAX_THROTTLE_RETRIES) {
        throw error;
      }
    }

    const delay = Math.min(INITIAL_BACKOFF_MS * 2 ** attempt, MAX_BACKOFF_MS);
    await sleep(delay);
  }
}

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// The existing shopifyAdminFetch helper takes a raw query string (no variables),
// so we inline collection IDs and cursors using JSON.stringify for safe quoting.
const buildCollectionProductsQuery = (collectionId, cursor) => `
  query {
    collection(id: ${JSON.stringify(collectionId)}) {
      id
      title
      products(first: ${SHOPIFY_PAGE_SIZE}${cursor ? `, after: ${JSON.stringify(cursor)}` : ""}) {
        pageInfo { hasNextPage endCursor }
        nodes {
          id
          handle
          title
          vendor
          tags
          images(first: 1) { nodes { originalSrc: url } }
          variants(first: ${SHOPIFY_PAGE_SIZE}) {
            pageInfo { hasNextPage endCursor }
            nodes {
              id
              title
              sku
              price
              inventoryQuantity
              displayName
              inventoryPolicy
              updatedAt
              inventoryItem { id }
            }
          }
        }
      }
    }
  }
`;

const buildProductVariantsPageQuery = (productId, cursor) => `
  query {
    product(id: ${JSON.stringify(productId)}) {
      id
      variants(first: ${SHOPIFY_PAGE_SIZE}, after: ${JSON.stringify(cursor)}) {
        pageInfo { hasNextPage endCursor }
        nodes {
          id
          title
          sku
          price
          inventoryQuantity
          displayName
          inventoryPolicy
          updatedAt
          inventoryItem { id }
        }
      }
    }
  }
`;

// Fetch all products (with up to 250 variants inlined) for a single collection.
async function fetchProductsForCollection(collectionId, { onProductFetched } = {}) {
  const products = [];
  let cursor = null;

  do {
    const response = await withShopifyBackoff(() =>
      shopifyAdminFetch({ query: buildCollectionProductsQuery(collectionId, cursor) })
    );

    if (response?.errors?.length) {
      throw new Error(response.errors[0]?.message || "Shopify Admin GraphQL error");
    }

    const connection = response?.data?.collection?.products;
    if (!connection) break;

    for (const product of connection.nodes) {
      products.push(product);
      onProductFetched?.();
    }

    cursor = connection.pageInfo?.hasNextPage ? connection.pageInfo.endCursor : null;
  } while (cursor);

  return products;
}

// Fetch every product (with up to 250 variants each) across all given collections.
// Products that appear in multiple collections are deduplicated by GID.
export async function fetchProductsForCollections(collectionIds, { onProductFetched } = {}) {
  const productsById = new Map();

  for (const collectionId of collectionIds) {
    const products = await fetchProductsForCollection(collectionId, { onProductFetched });
    for (const product of products) {
      if (!productsById.has(product.id)) {
        productsById.set(product.id, product);
      }
    }
  }

  return Array.from(productsById.values());
}

// If a product has more than 250 variants, paginate the remaining variants and
// merge them into `product.variants.nodes`. For products within the 250-variant
// fast path this is a no-op and no additional requests are made.
export async function completeVariantsIfNeeded(product) {
  const variantsConnection = product?.variants;
  if (!variantsConnection?.pageInfo?.hasNextPage) return product;

  const merged = [...(variantsConnection.nodes || [])];
  let cursor = variantsConnection.pageInfo.endCursor;

  while (cursor) {
    const response = await withShopifyBackoff(() =>
      shopifyAdminFetch({ query: buildProductVariantsPageQuery(product.id, cursor) })
    );

    if (response?.errors?.length) {
      throw new Error(response.errors[0]?.message || "Shopify Admin GraphQL error");
    }

    const page = response?.data?.product?.variants;
    if (!page) break;

    merged.push(...(page.nodes || []));

    if (merged.length >= MAX_VARIANTS_PER_PRODUCT) {
      merged.length = MAX_VARIANTS_PER_PRODUCT;
      break;
    }

    cursor = page.pageInfo?.hasNextPage ? page.pageInfo.endCursor : null;
  }

  return {
    ...product,
    variants: {
      nodes: merged,
      pageInfo: { hasNextPage: false, endCursor: null },
    },
  };
}
